import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Bot, Workflow, BrainCircuit, BarChart3, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { aiCapabilities } from "../mock";

const iconMap = { Bot, Workflow, BrainCircuit, BarChart3 };

export default function AICapabilities() {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const headlineY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="ai" ref={ref} className="relative pt-8 pb-14 lg:pt-10 lg:pb-20">
      <div className="absolute inset-0 section-tint pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <motion.div style={{ y: headlineY }} className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5"
            >
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
                <Sparkles className="w-3.5 h-3.5" />
              </motion.div>
              AI-First Capabilities
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-3xl md:text-5xl font-bold leading-tight text-[#111]"
            >
              The intelligence layer behind{" "}
              <span className="brand-gradient-text">modern enterprises</span>
            </motion.h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 text-[#555] text-base lg:text-lg leading-relaxed"
          >
            From autonomous agents to predictive intelligence, we engineer systems
            that think, decide and execute &mdash; unlocking measurable business outcomes.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {aiCapabilities.map((c, i) => {
            const Icon = iconMap[c.icon] || Bot;
            return <CapabilityCard key={c.title} c={c} i={i} Icon={Icon} />;
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mt-14 rounded-3xl border border-[#E5E5E5] bg-white p-8 lg:p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 dot-pattern opacity-30 pointer-events-none" />
          <div className="relative grid lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { label: "AI & Automation Experts", sub: "Production-grade AI systems" },
              { label: "Business Process Transformation", sub: "Cut manual effort by 60%+" },
              { label: "Custom Software Development", sub: "Tailor-built for your workflow" },
              { label: "Digital Growth Partner", sub: "Outcomes you can measure" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="flex items-start gap-3 group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#73100D]/10 grid place-items-center shrink-0 group-hover:bg-[#73100D] transition-colors duration-300">
                  <CheckCircle2 className="w-4.5 h-4.5 text-[#73100D] group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#111]">{item.label}</div>
                  <div className="text-xs text-[#666] mt-0.5">{item.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CapabilityCard({ c, i, Icon }) {
  const ref = React.useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left, y = e.clientY - r.top;
    const rx = ((y / r.height) - 0.5) * -10;
    const ry = ((x / r.width) - 0.5) * 10;
    el.style.setProperty("--rx", rx + "deg");
    el.style.setProperty("--ry", ry + "deg");
    el.style.setProperty("--mx", (x / r.width * 100) + "%");
    el.style.setProperty("--my", (y / r.height * 100) + "%");
  };
  const onLeave = () => {
    const el = ref.current; if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="group relative rounded-2xl bg-white border border-[#E5E5E5] p-7 transition-all duration-500 hover:border-[#73100D]/30 hover:shadow-[0_18px_50px_-18px_rgba(115,16,13,0.18)] overflow-hidden"
      style={{ transform: "perspective(1000px) rotateX(var(--rx,0)) rotateY(var(--ry,0)) translateZ(0)", transition: "transform 0.25s ease-out, border-color 0.4s, box-shadow 0.4s" }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: "radial-gradient(280px circle at var(--mx,50%) var(--my,50%), rgba(115,16,13,0.10), transparent 60%)" }}
      />
      <div className="relative">
        {/* Continuously floating icon */}
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
          className="relative w-12 h-12 grid place-items-center mb-5"
        >
          {/* Spinning conic ring around the icon */}
          <div className="absolute -inset-1 rounded-xl icon-ring opacity-40 spin-slow" />
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#73100D] to-[#5E0000]" />
          <Icon className="relative w-5 h-5 text-white" />
        </motion.div>
        <h3 className="text-lg font-semibold text-[#111]">{c.title}</h3>
        <p className="mt-2 text-sm text-[#555] leading-relaxed">{c.desc}</p>
        <div className="mt-5 pt-4 border-t border-[#E5E5E5] flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.18em] text-[#73100D] font-semibold font-mono">
            {c.metric}
          </span>
          <motion.div animate={{ x: [0, 4, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowRight className="w-4 h-4 text-[#73100D]" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
